import {Event, news, NewsEvent, strategies, StrategyEvent} from "../stores/events";
import {createSlice, PayloadAction} from '@reduxjs/toolkit'
import SeedGenerator from "../libs/SeedGenerator";
import TickerGenerator from "../libs/TickerGenerator";
import {MAX_POINTS} from "../components/Charts";
import {BR500, DPC, INVD, MSLA, SMD} from "../stores/symbols";

interface MarketState {
    seed: string,
    tickers: Record<string, {name: string, value: number, volatility: number}>,
    cash: number,
    portfolio: Record<string, {fractions: number}>,
    news: NewsEvent[],
    events: Event[],
    history: Record<string, number[]>,
    strategies: StrategyEvent[]
}

const getSeed = () => {

    const currentPath = window.location.pathname;
    let seed;
    if (currentPath.split('/')[1].length > 0) {
        const seedPath = currentPath.split('/')[1].replace(/\W/g, '');
        seed = seedPath.length > 0 ? seedPath.substring(0, 6) : 'DEFAULT';
    } else {
        seed = new SeedGenerator().seed();
    }

    return seed;

}

const seed = getSeed();
const generator = new TickerGenerator(seed);

const generateHistory = (initialValue: number, volatility: number) => {
    // @ts-ignore
    return [...Array(MAX_POINTS).keys()].reduce((current, _) => {
        current.push(generator.next(current[current.length - 1], volatility));
        return current;
    }, [initialValue]);
}

const initialVolatility = {
    MSLA: 0.1,
    DPC: 0.1,
    BR500: 0.02,
    INVD: 0.1,
    SMD: 0.1
}

const initialHistory = {
    // @ts-ignore
    MSLA: generateHistory(890, initialVolatility[MSLA]),
    DPC: generateHistory(23456, initialVolatility[DPC]),
    BR500: generateHistory(1234, initialVolatility[BR500]),
    INVD: generateHistory(124, initialVolatility[INVD]),
    SMD: generateHistory(67, initialVolatility[SMD]),
};

const initialTickers = {
    MSLA: {name: 'Mesla Inc', value: initialHistory[MSLA].slice(-1)[0], volatility: initialVolatility[MSLA]},
    DPC: {name: 'Dipcoin', value: initialHistory[DPC].slice(-1)[0], volatility: initialVolatility[DPC]},
    BR500: {name: 'Boring&Rich 500', value: initialHistory[BR500].slice(-1)[0], volatility: initialVolatility[BR500]},
    INVD: {name: 'Invidia Corporation', value: initialHistory[INVD].slice(-1)[0], volatility: initialVolatility[INVD]},
    SMD: {name: 'Standard Micro Devices', value: initialHistory[SMD].slice(-1)[0], volatility: initialVolatility[SMD]},
};

const initialMarket: MarketState = {
    seed: seed,
    tickers: initialTickers,
    cash: 100000,
    portfolio: {},
    news: news,
    events: [],
    history: initialHistory,
    strategies: strategies
};

const tickerGenerator = new TickerGenerator(initialMarket.seed);

export const marketSlice = createSlice({
    name: 'market',
    initialState: initialMarket,
    reducers: {
        buyTicker: (state, action: PayloadAction<{ticker: {symbol: string, amount: number}}>) => {

            if (state.cash <= 0) return;

            const symbol = action.payload.ticker.symbol;
            const amount = action.payload.ticker.amount;
            const currentPortfolio = state.portfolio;

            const ticker = currentPortfolio[symbol];
            const unitValue = state.tickers[symbol]?.value || Infinity;
            const fractions = amount / unitValue;

            if (ticker) {
                ticker.fractions += fractions;
            } else {
                currentPortfolio[symbol] = {fractions: fractions};
            }

            state.cash -= amount;

        },
        sellTicker: (state, action: PayloadAction<{ticker: {symbol: string, amount: number}}>) => {

            const symbol = action.payload.ticker.symbol;
            const amount = action.payload.ticker.amount;
            const currentPortfolio = state.portfolio;

            const ticker = currentPortfolio[symbol];
            const unitValue = state.tickers[symbol]?.value || Infinity;
            const fractions = amount / unitValue;

            if (ticker.fractions >= fractions) {
                ticker.fractions -= fractions;
                state.cash += amount;
            }

        },
        updateTicker: (state, action: PayloadAction<{ticker: {symbol: string}}>) => {

            const symbol = action.payload.ticker.symbol;
            const ticker = state.tickers[symbol];
            const currentPrice = state.history[symbol][state.history[symbol].length - 1]
            let nextPrice = tickerGenerator.next(currentPrice, ticker.volatility);

            // Apply market events influence on ticker:
            state.events
                .filter(e => e.influence.symbol === symbol && e.influence.startsIn === 0 && e.influence.endsIn > 0)
                .forEach(e => nextPrice *= (1 + e.influence.amount));

            // Apply strategy events influence on ticker:
            state.strategies
                .filter(e => e.active && e.influence.symbol === symbol && e.influence.startsIn === 0 && e.influence.endsIn > 0)
                .forEach(e => nextPrice *= (1 + e.influence.amount));

            state.tickers[symbol].value = nextPrice;
            state.history[symbol].push(nextPrice);

        },
        updateEvents: (state) => {
            state.events.forEach(e => {
                if (e.influence.startsIn > 0) {
                    e.influence.startsIn -= 1;
                    return;
                }
                if (e.influence.endsIn > 0) e.influence.endsIn -= 1;
            });
            state.strategies.forEach(e => {
                if (!e.active) return;
                if (e.influence.startsIn > 0) {
                    e.influence.startsIn -= 1;
                    return;
                }
                if (e.influence.endsIn > 0) e.influence.endsIn -= 1;
            });
        },
        addEvent: (state, action: PayloadAction<{event: NewsEvent}>) => {
            state.events.unshift(action.payload.event);
            state.news.unshift(action.payload.event);
            return;
        },
        addStrategy: (state, action: PayloadAction<{strategy: {key: number}}>) => {
            const strategy = state.strategies[action.payload.strategy.key];
            state.cash -= strategy.cost;
            strategy.active = true;
        }
    },
});

export const { buyTicker, sellTicker, updateTicker, updateEvents, addEvent, addStrategy } = marketSlice.actions;

export {initialMarket};

export default marketSlice.reducer;